import { useState, useEffect } from 'react';
import {
  Calendar, Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink,
  Loader2, RefreshCw, Copy, MapPin, CalendarDays
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import adminApi from '../../services/adminApi';

import { PageTitle } from './shared';
import { ImagePicker } from './shared/ImagePicker';

const EVENT_TYPES = [
  { value: 'tournament', label: 'Tournament', color: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300' },
  { value: 'seminar', label: 'Seminar', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300' },
  { value: 'belt_test', label: 'Belt Test', color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300' },
  { value: 'open_house', label: 'Open House', color: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' },
  { value: 'special', label: 'Special Event', color: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300' },
];

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toLocalDateTimeString = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // Format as YYYY-MM-DDTHH:mm for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const isUpcoming = (dateString) => {
  return new Date(dateString) >= new Date();
};

const getEventTypeInfo = (type) => {
  return EVENT_TYPES.find(t => t.value === type) || { label: type, color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' };
};

const EventCard = ({ event, onEdit, onDelete, onDuplicate, onToggleVisibility }) => {
  const typeInfo = getEventTypeInfo(event.event_type);
  const upcoming = isUpcoming(event.date);

  return (
    <Card className={`${!event.is_public ? 'border-dashed opacity-75' : ''} ${!upcoming ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
              {!upcoming && <Badge variant="secondary">Past</Badge>}
              {!event.is_public && (
                <Badge variant="outline" className="text-gray-500 dark:text-gray-400">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Hidden
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{event.title}</h3>
          </div>
          {event.image_url && (
            <img
              src={event.image_url}
              alt={event.title}
              className="w-16 h-16 object-cover rounded-lg ml-3"
            />
          )}
        </div>

        {/* Details */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{event.description}</p>

        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-red-600" />
            <span>{formatDateTime(event.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-red-600" />
            <span className="truncate">{event.location}</span>
          </div>
          {event.registration_url && (
            <div className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-blue-600" />
              <span className={event.registration_open ? 'text-green-600' : 'text-gray-400'}>
                Registration {event.registration_open ? 'Open' : 'Closed'}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(event)} className="flex-1">
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleVisibility(event)}
            title={event.is_public ? 'Hide from public' : 'Show to public'}
          >
            {event.is_public ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDuplicate(event)}
            title="Create copy"
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(event)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    end_date: '',
    location: '',
    event_type: 'special',
    registration_url: '',
    registration_open: true,
    image_url: '',
    is_public: true,
  });

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/api/admin/events?include_past=${showPast}`);
      if (!response.ok) throw new Error('Failed to load events');
      const data = await response.json();
      setEvents(data);
      setError(null);
    } catch (err) {
      setError('Failed to load events');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [showPast]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      end_date: '',
      location: 'Owaza Martial Arts, 14726 County Line Rd, Spring Hill, FL 34610',
      event_type: 'special',
      registration_url: '',
      registration_open: true,
      image_url: '',
      is_public: true,
    });
  };

  const handleCreate = () => {
    resetForm();
    setEditingEvent({ isNew: true });
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: toLocalDateTimeString(event.date),
      end_date: event.end_date ? toLocalDateTimeString(event.end_date) : '',
      location: event.location || '',
      event_type: event.event_type || 'special',
      registration_url: event.registration_url || '',
      registration_open: event.registration_open ?? true,
      image_url: event.image_url || '',
      is_public: event.is_public ?? true,
    });
    setEditingEvent(event);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.date || !formData.location) {
      alert('Please fill in title, date, and location');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        registration_url: formData.registration_url || null,
        image_url: formData.image_url || null,
      };

      let response;
      if (editingEvent.isNew) {
        response = await adminApi.post('/api/admin/events', payload);
      } else {
        response = await adminApi.put(`/api/admin/events/${editingEvent.id}`, payload);
      }
      if (!response.ok) throw new Error('Failed to save');

      await loadEvents();
      setEditingEvent(null);
    } catch (err) {
      console.error('Failed to save event:', err);
      alert('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingEvent) return;

    try {
      const response = await adminApi.delete(`/api/admin/events/${deletingEvent.id}`);
      if (!response.ok) throw new Error('Failed to delete');
      await loadEvents();
      setDeletingEvent(null);
    } catch (err) {
      console.error('Failed to delete event:', err);
      alert('Failed to delete event');
    }
  };

  const handleDuplicate = async (event) => {
    try {
      const response = await adminApi.post(`/api/admin/events/${event.id}/duplicate`, {});
      if (!response.ok) throw new Error('Failed to duplicate');
      await loadEvents();
    } catch (err) {
      console.error('Failed to duplicate event:', err);
      alert('Failed to duplicate event');
    }
  };

  const handleToggleVisibility = async (event) => {
    try {
      const response = await adminApi.put(`/api/admin/events/${event.id}`, {
        is_public: !event.is_public,
      });
      if (!response.ok) throw new Error('Failed to toggle');
      await loadEvents();
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Public Events"
        description="Manage tournaments, seminars, belt tests, and special events"
        icon={<CalendarDays className="h-6 w-6" />}
        actions={
          <div className="flex gap-2">
            <Button onClick={loadEvents} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleCreate} size="sm" variant="adminPrimary">
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </div>
        }
      />

      {/* Filter */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">Show:</span>
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-background">
          <button
            onClick={() => setShowPast(false)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors admin-touch:min-h-[44px] ${
              !showPast ? 'bg-gradient-to-r from-red-800 to-red-900 text-amber-50' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setShowPast(true)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors admin-touch:min-h-[44px] ${
              showPast ? 'bg-gradient-to-r from-red-800 to-red-900 text-amber-50' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            All Events
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30">
          <CardContent className="p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Events Grid */}
      {events.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onEdit={handleEdit}
              onDelete={setDeletingEvent}
              onDuplicate={handleDuplicate}
              onToggleVisibility={handleToggleVisibility}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No Events</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Create your first event to display on the public events page.
            </p>
            <Button onClick={handleCreate} variant="adminPrimary">
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingEvent} onOpenChange={(open) => !open && setEditingEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEvent?.isNew ? 'Create Event' : 'Edit Event'}
            </DialogTitle>
            <DialogDescription>
              {editingEvent?.isNew
                ? 'Create a new public event'
                : 'Update event details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Event title..."
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Event description..."
                className="min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Type</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Event location..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date & Time (optional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Registration URL (optional)</Label>
              <Input
                value={formData.registration_url}
                onChange={(e) => setFormData({ ...formData, registration_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.registration_open}
                  onCheckedChange={(checked) => setFormData({ ...formData, registration_open: checked })}
                />
                <Label className="cursor-pointer">Registration Open</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                />
                <Label className="cursor-pointer">Visible to Public</Label>
              </div>
            </div>

            <div>
              <ImagePicker
                label="Event Image (optional)"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                context="events"
                previewSize="lg"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="adminOutline" onClick={() => setEditingEvent(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} variant="adminPrimary">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Event'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingEvent} onOpenChange={(open) => !open && setDeletingEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingEvent?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EventsManager;
